/**
 * Amazon Ads — DSP AUDIO_VIDEO BETA Runtime Schema Introspection
 * (Story 19.16.1, Task 4)
 *
 * Mirrors `dsp-audience-introspection.ts` for Amazon's `audio-and-video`
 * report-type which is documented as BETA on Amazon's official docs page (the
 * page returns skeleton-only via WebFetch — verified May 2026).
 *
 * Bright Analytics V3 (May 2026) confirms a 4-metric + 3-dimension floor:
 *   - Metrics: audioImpressions / audioCompleteListens / videoCTVImpressions /
 *     videoCTVCompletes
 *   - Dimensions: creativeType / audioFormat / videoFormat
 *
 * The same defensive layers as AUDIENCE apply (cross-tenant cache key,
 * column-name regex defense, TTL-driven re-introspection drift detection).
 *
 * AUDIO_VIDEO-specific addition (AC #10): 4xx response bodies matching
 * `/beta/i` map to `provider_unavailable` retryable=true via the parser in
 * `errors.ts`. The runtime introspection + 7-day TTL absorbs Amazon's BETA
 * schema drift without code changes.
 */

import type { AmazonAdsKvApi } from "./shared/report-orchestration";
import { AMAZON_ADS_DSP_COLUMN_NAME_REGEX, AMAZON_ADS_DSP_INTROSPECTED_SCHEMA_TTL_SEC } from "./dsp-audience-introspection";

// ============================================================================
// Constants — Bright-Analytics-confirmed-floor (compile-time-frozen)
// ============================================================================

export const AMAZON_ADS_DSP_AUDIO_VIDEO_FLOOR_METRICS = [
  "audioImpressions",
  "audioCompleteListens",
  "videoCTVImpressions",
  "videoCTVCompletes",
] as const;

export const AMAZON_ADS_DSP_AUDIO_VIDEO_FLOOR_DIMENSIONS = [
  "creativeType",
  "audioFormat",
  "videoFormat",
] as const;

export type AudioVideoFloorMetricName =
  (typeof AMAZON_ADS_DSP_AUDIO_VIDEO_FLOOR_METRICS)[number];
export type AudioVideoFloorDimensionName =
  (typeof AMAZON_ADS_DSP_AUDIO_VIDEO_FLOOR_DIMENSIONS)[number];

// Known enum values per AC #10. Enum-extensibility tolerance applies — unknown
// values pass through with a warning log (mirrors TECHNICAL pattern).
export const AMAZON_ADS_DSP_AUDIO_VIDEO_CREATIVE_TYPES = [
  "AUDIO",
  "VIDEO_CTV",
  "VIDEO_OLV",
  "DISPLAY",
] as const;
export const AMAZON_ADS_DSP_AUDIO_VIDEO_AUDIO_FORMATS = ["15s", "30s", "60s"] as const;
export const AMAZON_ADS_DSP_AUDIO_VIDEO_VIDEO_FORMATS = [
  "15s",
  "30s",
  "60s",
  "90s+",
] as const;

/**
 * Story 19.16.1 Task 4 — KV cache key. Mirrors AUDIENCE pattern but with
 * `DSP_AUDIO_VIDEO` suffix. Cross-tenant isolation invariant.
 */
export function buildAudioVideoSchemaCacheKey(args: {
  readonly tenantId: string;
  readonly integrationId: string;
}): string {
  if (!args.tenantId || typeof args.tenantId !== "string") {
    throw new Error(
      "buildAudioVideoSchemaCacheKey: tenantId is required (cross-tenant cache isolation)"
    );
  }
  if (!args.integrationId || typeof args.integrationId !== "string") {
    throw new Error("buildAudioVideoSchemaCacheKey: integrationId is required");
  }
  return `amazon_ads_dsp_introspected_schema:${args.tenantId}:${args.integrationId}:DSP_AUDIO_VIDEO`;
}

// ============================================================================
// Introspection helpers
// ============================================================================

export interface IntrospectAudioVideoSchemaArgs {
  readonly kv: AmazonAdsKvApi;
  readonly tenantId: string;
  readonly integrationId: string;
  readonly observedColumns: ReadonlyArray<string>;
}

export interface IntrospectAudioVideoSchemaResult {
  readonly persisted: ReadonlyArray<string>;
  readonly rejected: ReadonlyArray<string>;
}

export async function introspectAudioVideoSchema(
  args: IntrospectAudioVideoSchemaArgs
): Promise<IntrospectAudioVideoSchemaResult> {
  // Round 3 Vuln 2 — validate tenantId/integrationId BEFORE any log emission
  // (mirrors AUDIENCE pattern).
  if (!args.tenantId || typeof args.tenantId !== "string") {
    throw new Error(
      "introspectAudioVideoSchema: tenantId is required (cross-tenant cache isolation)"
    );
  }
  if (!args.integrationId || typeof args.integrationId !== "string") {
    throw new Error(
      "introspectAudioVideoSchema: integrationId is required"
    );
  }

  const persisted: string[] = [];
  const rejected: string[] = [];

  for (const col of args.observedColumns) {
    if (typeof col !== "string") {
      rejected.push(String(col));
      continue;
    }
    if (AMAZON_ADS_DSP_COLUMN_NAME_REGEX.test(col)) {
      persisted.push(col);
    } else {
      rejected.push(col);
    }
  }

  if (rejected.length > 0) {
    logEvent("amazon_ads_dsp_audio_video_schema_invalid_column_name", {
      tenantId: args.tenantId,
      integrationId: args.integrationId,
      rejected,
    });
  }

  if (persisted.length === 0) {
    logEvent("amazon_ads_dsp_audio_video_introspection_empty_using_fallback", {
      tenantId: args.tenantId,
      integrationId: args.integrationId,
    });
    return { persisted, rejected };
  }

  const key = buildAudioVideoSchemaCacheKey({
    tenantId: args.tenantId,
    integrationId: args.integrationId,
  });
  await args.kv.put(
    key,
    JSON.stringify(persisted),
    AMAZON_ADS_DSP_INTROSPECTED_SCHEMA_TTL_SEC
  );
  logEvent("amazon_ads_dsp_introspected_schema_persisted", {
    tenantId: args.tenantId,
    integrationId: args.integrationId,
    reportTypeId: "DSP_AUDIO_VIDEO",
    columns: persisted,
  });
  // BETA observability — fires on every persist so operators can monitor BETA
  // surface usage even before Amazon GA's the schema.
  logEvent("amazon_ads_dsp_v3_beta_observed", {
    tenantId: args.tenantId,
    integrationId: args.integrationId,
    reportTypeId: "DSP_AUDIO_VIDEO",
  });
  return { persisted, rejected };
}

export async function loadIntrospectedAudioVideoSchema(args: {
  readonly kv: AmazonAdsKvApi;
  readonly tenantId: string;
  readonly integrationId: string;
}): Promise<ReadonlyArray<string> | null> {
  const key = buildAudioVideoSchemaCacheKey({
    tenantId: args.tenantId,
    integrationId: args.integrationId,
  });
  const raw = await args.kv.get(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter(
      (c): c is string =>
        typeof c === "string" && AMAZON_ADS_DSP_COLUMN_NAME_REGEX.test(c)
    );
  } catch {
    return null;
  }
}

export interface AudioVideoSchemaDrift {
  readonly added: ReadonlyArray<string>;
  readonly removed: ReadonlyArray<string>;
}

export function compareAudioVideoSchemaForDrift(
  prevColumns: ReadonlyArray<string>,
  newColumns: ReadonlyArray<string>
): AudioVideoSchemaDrift {
  const prev = new Set(prevColumns);
  const next = new Set(newColumns);
  const added = newColumns.filter((c) => !prev.has(c));
  const removed = prevColumns.filter((c) => !next.has(c));
  if (added.length > 0 || removed.length > 0) {
    logEvent("amazon_ads_dsp_audio_video_schema_drift_detected", {
      added,
      removed,
    });
  }
  return { added, removed };
}

/**
 * Enum-extensibility tolerance per AC #10 + Round 2 Pre-mortem D — unknown
 * values pass through with a warning rather than failing the record.
 */
export function validateAudioVideoEnumValue(
  field: "creativeType" | "audioFormat" | "videoFormat",
  value: string
): { valid: true; isKnownValue: boolean } {
  const knownValues =
    field === "creativeType"
      ? AMAZON_ADS_DSP_AUDIO_VIDEO_CREATIVE_TYPES
      : field === "audioFormat"
        ? AMAZON_ADS_DSP_AUDIO_VIDEO_AUDIO_FORMATS
        : AMAZON_ADS_DSP_AUDIO_VIDEO_VIDEO_FORMATS;
  if ((knownValues as readonly string[]).includes(value)) {
    return { valid: true, isKnownValue: true };
  }
  logEvent("amazon_ads_dsp_audio_video_unknown_enum_value", { field, value });
  return { valid: true, isKnownValue: false };
}

function logEvent(event: string, details: Record<string, unknown>): void {
  console.log(
    JSON.stringify({
      event,
      ...details,
      timestamp: new Date().toISOString(),
    })
  );
}
